/**
 * Pure helpers for an application element's version history (GET
 * …/activity/applications/<element>/history): which feedback points are
 * picked, how a move's state changed between two of them, and the summary
 * sentence's numbers. Points arrive oldest first, numbered 1..n across
 * feedback and upload points; a point is picked by its `number`.
 */
import { daysBetween } from './activityFormat';

// Order of the three move states, for "rose" and "fell".
const RANK = { not_yet: 0, partly: 1, shown: 2 };

export const isFeedback = (point) => point?.kind === 'feedback';
export const isUpload = (point) => point?.kind === 'upload';

/** The feedback points, oldest first. */
export const feedbackPoints = (history) => (history?.points || []).filter(isFeedback);

/** The upload points, oldest first. */
export const uploadPoints = (history) => (history?.points || []).filter(isUpload);

/**
 * The picks a history opens on: the first and the latest feedback points,
 * just the one when there is one, none when there are none.
 */
export function defaultPicks(history) {
  const feedback = feedbackPoints(history);
  if (!feedback.length) return [];
  if (feedback.length === 1) return [feedback[0].number];
  return [feedback[0].number, feedback[feedback.length - 1].number];
}

/**
 * The picks after a click on feedback point `number`: with a pair picked a
 * click starts a new pair; with one picked it completes the pair, earlier
 * first. Clicking the one already picked leaves it picked alone.
 */
export function nextPicks(picks, number) {
  if (picks.length !== 1) return [number];
  if (picks[0] === number) return picks;
  return [picks[0], number].sort((a, b) => a - b);
}

/** 'gain', 'loss' or 'same' from one state to another; 'same' when either is unknown. */
export function stateChange(before, after) {
  if (!(before in RANK) || !(after in RANK)) return 'same';
  if (RANK[after] > RANK[before]) return 'gain';
  if (RANK[after] < RANK[before]) return 'loss';
  return 'same';
}

/**
 * The summary's numbers for the pair `from`, `to` (feedback points, `from`
 * the earlier): moves shown at each, whole London days between them, and
 * how many feedback requests the span covers, both ends included.
 */
export function pairSummary(history, from, to) {
  const across = feedbackPoints(history).filter((p) => p.number >= from.number && p.number <= to.number).length;
  return {
    from: from.moves_shown,
    to: to.moves_shown,
    days: daysBetween(from.at, to.at),
    across,
  };
}
